/**
 * A list's rows as a board (docs/list-standard.md §12): a lane for each value of one column, each
 * row a card in its lane, each lane headed by how many are in it and what they add up to.
 *
 * One rule, the list's: the figures are never the page's. On a complete source a lane counts and
 * sums every row the view left — the board is not paged — and on a `query` source the route counted
 * them (`totals.lanes`) over every row the query matched; the page's cards are what is in hand, and
 * a lane says when it holds fewer than it counts. Pure, so each is proved without a browser.
 */
import { rowsOf } from "./manifest.js";
import { valueLabel } from "./derive.js";
import { sumOf } from "./export.js";
import { viewShown } from "./list-views.js";
/**
 * The lanes a board draws, with their cards and figures. `rows` are the rows the view left (a
 * complete source, unpaged) or the page (a `query` source, whose `totals` carry the figures).
 */
export function lanesOf(manifest, rows, i18n, server) {
    const board = manifest.board;
    if (!board || !board.values)
        return { ready: false, lanes: [], unplaced: 0 };
    const column = (manifest.columns ?? []).find((c) => c.key === board.by) ?? { key: board.by, label: '' };
    const worded = board.values_key ? { ...column, values_key: board.values_key } : column;
    const keys = new Set(board.values.map(String));
    const lanes = board.values.map((value) => {
        const key = String(value);
        const mine = rows.filter((r) => String(r[board.by] ?? '') === key);
        const counted = server ? server.totals?.lanes?.[key] : undefined;
        const count = server ? (typeof counted?.count === 'number' ? counted.count : null) : mine.length;
        const sum = !board.sum ? null
            : server ? (counted?.sum === undefined ? null : counted.sum)
                : sumOf(mine.map((r) => r[board.sum]));
        return { value, key, label: board.labels?.[key] ?? valueLabel(worded, value, i18n), rows: mine, count, sum };
    });
    const unplaced = rows.filter((r) => !keys.has(String(r[board.by] ?? ''))).length;
    return { ready: true, lanes, unplaced };
}
/**
 * The manifest with its lanes read from the route `board.values_from` names: its rows, in its
 * order, each named by its `label`. Unchanged when the answer is not rows — the board then waits,
 * rather than drawing lanes guessed from the data.
 */
export function resolveLanes(manifest, answer) {
    const board = manifest.board;
    const from = board?.values_from;
    if (!board || !from)
        return manifest;
    const read = rowsOf(answer, { screen: manifest.screen, kind: 'list', source: { path: from.path, ...(from.rows ? { rows: from.rows } : {}) } });
    if (!read.ok)
        return manifest;
    const values = [];
    const labels = {};
    for (const row of read.rows) {
        const value = row[from.value];
        if (value === undefined || value === null || typeof value === 'object' || values.some((v) => String(v) === String(value)))
            continue;
        values.push(value);
        const label = row[from.label];
        if (label !== undefined && label !== null && label !== '')
            labels[String(value)] = String(label);
    }
    const { values_from: _read, ...rest } = board;
    return { ...manifest, board: { ...rest, values, labels } };
}
/**
 * Whether the board is in view: the viewer's own choice for this screen, else what the list opens as
 * (`viewShown`, list-views.ts) — the board's `opens` only on a table layout (A1).
 */
export function boardShown(manifest, chosen) {
    return viewShown(manifest, chosen, ['board']) === 'board';
}
//# sourceMappingURL=board.js.map